import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { casesAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Lock, PenTool } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import SignatureModal from '@/components/SignatureModal';

export default function ClientTermSign() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [existingTerm, setExistingTerm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedComm, setAcceptedComm] = useState(false);
  const [showSignature, setShowSignature] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const caseRes = await casesAPI.get(caseId);
        setCaseData(caseRes.data);
        try {
          const termRes = await casesAPI.getTerm(caseId);
          setExistingTerm(termRes.data);
        } catch {}
      } catch (err) {
        toast.error('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [caseId]);

  const canOpenSignature = acceptedTerms && acceptedComm && !existingTerm;

  const handleSignatureConfirm = async (signatureDataUrl) => {
    setShowSignature(false);
    setSigning(true);
    try {
      const res = await casesAPI.signTerm(caseId, {
        acceptedTerms: true,
        acceptedComm: true,
        signatureImage: signatureDataUrl,
        city: 'Joao Pessoa',
      });
      setExistingTerm(res.data.term);
      toast.success('Pronto! Seu Termo de Filiacao foi assinado. Iremos dar sequencia no processo.');
      // Redirect to dashboard after 1.8s
      setTimeout(() => {
        navigate('/client');
      }, 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao assinar termo');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="rn-spinner" style={{ width: 32, height: 32 }} />
      </div>
    );
  }

  const isSigned = !!existingTerm;

  return (
    <div className="space-y-6" data-testid="client-term-sign">
      {/* Signature Modal */}
      {showSignature && (
        <SignatureModal
          onConfirm={handleSignatureConfirm}
          onClose={() => setShowSignature(false)}
        />
      )}

      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/client')} className="p-2 rounded-xl hover:bg-white/5 transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="font-display text-2xl font-semibold">
            {isSigned ? 'Termo de Filiacao' : 'Assinatura do Termo de Filiacao'}
          </h1>
          <p className="text-sm text-white/50">{caseData?.docType}: {caseData?.docFormatted}</p>
        </div>
        {isSigned && (
          <span className="ml-auto badge-success flex items-center gap-1 text-sm px-3 py-1">
            <CheckCircle size={14} /> Concluido
          </span>
        )}
      </div>

      {/* === SIGNED STATE === */}
      {isSigned && (
        <div className="rn-card border-[rgba(0,255,163,0.25)]">
          <div className="text-center py-10">
            <CheckCircle size={72} className="mx-auto text-[var(--rn-green)] mb-5" />
            <h2 className="font-display text-xl font-semibold text-[var(--rn-green)] mb-3">
              Pronto! Seu Termo de Filiacao foi assinado.
            </h2>
            <p className="text-sm text-white/50 max-w-md mx-auto">
              Iremos dar sequencia no processo. Acompanhe o andamento pelo seu painel.
            </p>
          </div>

          {/* Permanently disabled button */}
          <button
            disabled
            className="w-full mt-4 py-3 rounded-xl bg-white/[0.06] text-white/40 cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="term-signing-submit-button"
          >
            <Lock size={16} /> Termo Assinado
          </button>
        </div>
      )}

      {/* === UNSIGNED STATE === */}
      {!isSigned && (
        <div className="rn-card" data-testid="term-signing-term">
          {/* Term content preview */}
          <div className="max-h-[400px] overflow-y-auto pr-2 mb-6 rounded-xl bg-white/[0.02] border border-white/10 p-5">
            <h3 className="font-display text-base font-semibold mb-1 text-center">FICHA DE INSCRICAO ASSOCIATIVA</h3>
            <p className="text-xs text-white/40 text-center mb-5">DECLARACAO / AUTORIZACAO</p>

            <div className="space-y-1 mb-5">
              <p className="text-xs text-white/50 font-semibold uppercase tracking-wide">Dados Pessoais</p>
              <p className="text-sm text-white/70">Nome Completo: <span className="text-white/90">{caseData?.user?.name || 'N/A'}</span></p>
              <p className="text-sm text-white/70">CPF: <span className="text-white/90 font-mono">{caseData?.docFormatted}</span></p>
              <p className="text-sm text-white/70">E-Mail: <span className="text-white/90">{caseData?.user?.email || 'N/A'}</span></p>
              <p className="text-sm text-white/70">Telefone/WhatsApp: <span className="text-white/90">{caseData?.user?.phone || 'N/A'}</span></p>
            </div>

            <div className="border-t border-white/10 pt-4">
              <p className="text-xs text-white/50 font-semibold uppercase tracking-wide mb-3">Declaracao</p>
              <div className="text-sm text-white/70 space-y-3 leading-relaxed">
                <p>
                  Por meio do presente instrumento, venho requerer minha inscricao como associado(a) desta Associacao.
                  Ao assinar este termo, declaro estar plenamente ciente do inteiro teor do estatuto social da Associacao,
                  bem como dos direitos e deveres inerentes a qualidade de membro desta instituicao.
                </p>
                <p>
                  Declaro, outrossim, que consinto expressamente com a propositura de Acao de Obrigacao de Fazer com
                  Pedido de Tutela de Urgencia e Indenizacao por Danos Morais, em defesa de direitos difusos ou coletivos,
                  em meu nome, a ser ajuizada por esta Associacao, comprometendo-me, ainda, a efetuar os pagamentos
                  previamente acordados.
                </p>
              </div>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-4 border-t border-white/10 pt-5 mb-5">
            <div className="flex items-start gap-3">
              <Checkbox
                id="acceptedTerms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                data-testid="term-signing-checkbox-terms"
                className="border-white/20 data-[state=checked]:bg-[var(--rn-green)] data-[state=checked]:border-[var(--rn-green)] mt-0.5"
              />
              <label htmlFor="acceptedTerms" className="cursor-pointer">
                <p className="text-sm text-white/90 font-medium">Aceito os termos da ficha de filiacao</p>
                <p className="text-xs text-white/50 mt-0.5">
                  Declaro estar ciente do inteiro teor do estatuto social da Associacao e concordo com os termos apresentados acima.
                </p>
              </label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="acceptedComm"
                checked={acceptedComm}
                onCheckedChange={(checked) => setAcceptedComm(checked === true)}
                data-testid="term-signing-checkbox-comm"
                className="border-white/20 data-[state=checked]:bg-[var(--rn-green)] data-[state=checked]:border-[var(--rn-green)] mt-0.5"
              />
              <label htmlFor="acceptedComm" className="cursor-pointer">
                <p className="text-sm text-white/90 font-medium">Aceito receber meu relatorio por e-mail e WhatsApp, bem como orientacoes</p>
                <p className="text-xs text-white/50 mt-0.5">
                  Autorizo o envio de relatorios, atualizacoes sobre o processo e orientacoes relevantes por e-mail e WhatsApp.
                </p>
              </label>
            </div>
          </div>

          {/* Sign button → opens canvas modal */}
          <button
            onClick={() => setShowSignature(true)}
            disabled={!canOpenSignature || signing}
            className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
              canOpenSignature && !signing ? 'btn-primary' : 'bg-white/[0.06] text-white/30 cursor-not-allowed'
            }`}
            data-testid="term-signing-submit-button"
          >
            {signing ? (
              <><span className="rn-spinner" /> Assinando...</>
            ) : (
              <><PenTool size={18} /> Assinar agora</>
            )}
          </button>

          {(!acceptedTerms || !acceptedComm) && (
            <p className="text-xs text-white/40 text-center mt-3">
              Marque os dois aceites acima para habilitar a assinatura.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
